import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { platformApi, TenantListItem, COMP_TYPE_OPTIONS, compTypeLabel, type ImChannel } from '@/api/platform';

const STATUS_MAP: Record<string, { color: string; text: string }> = {
  ACTIVE: { color: 'green', text: '正常' },
  SUSPENDED: { color: 'orange', text: '已停用' },
  ARCHIVED: { color: 'default', text: '已归档' },
};

export default function TenantListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['tenants', search, status, page],
    queryFn: () => platformApi.listTenants({ search: search || undefined, status, page, size: 20 }),
  });

  const createMutation = useMutation({
    mutationFn: (values: { name: string; slug: string; compType?: string; imChannel?: ImChannel }) =>
      platformApi.createTenant(values),
    onSuccess: () => {
      message.success('租户创建成功');
      setCreateOpen(false);
      createForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '公司名称',
      dataIndex: 'name',
      render: (name: string, record: TenantListItem) => (
        <a onClick={() => navigate(`/tenants/${record.id}`)}>{name}</a>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      render: (slug: string) => <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded">{slug}</code>,
    },
    {
      title: '行业类型',
      dataIndex: 'compType',
      width: 120,
      render: (v: string | null) => compTypeLabel(v),
    },
    {
      title: 'IM 渠道',
      dataIndex: 'imChannel',
      width: 100,
      render: (v: ImChannel) => (
        <Tag color={v === 'wecom' ? 'green' : 'blue'}>{v === 'wecom' ? '企微' : '钉钉'}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => {
        const info = STATUS_MAP[s] || { color: 'default', text: s };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '员工数',
      dataIndex: 'employeeCount',
      width: 80,
      render: (n: number, r: TenantListItem) =>
        r.maxEmployees ? `${n} / ${r.maxEmployees}` : n,
    },
    {
      title: 'Token 余额',
      dataIndex: 'tokenBalance',
      width: 120,
      render: (v: string) => Number(v).toLocaleString(),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <>
      <Card
        title="租户管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            创建租户
          </Button>
        }
      >
        <Space className="mb-4">
          <Input
            placeholder="搜索公司名/slug"
            prefix={<SearchOutlined />}
            allowClear
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 240 }}
          />
          <Select
            placeholder="状态筛选"
            allowClear
            value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            style={{ width: 140 }}
            options={[
              { value: 'ACTIVE', label: '正常' },
              { value: 'SUSPENDED', label: '已停用' },
              { value: 'ARCHIVED', label: '已归档' },
            ]}
          />
        </Space>

        <Table
          rowKey="id"
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          pagination={{
            current: page,
            total: data?.total,
            pageSize: 20,
            showTotal: (t) => `共 ${t} 个租户`,
            onChange: setPage,
          }}
        />
      </Card>

      <Modal
        title="创建新租户"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(v) => createMutation.mutate(v)}
        >
          <Form.Item
            name="name"
            label="公司名称"
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input placeholder="如：示例科技有限公司" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="公司标识（slug）"
            rules={[{ required: true, message: '请输入 slug' }]}
            extra="唯一标识，用于子域名和登录，如 example-corp"
          >
            <Input placeholder="如：example-corp" />
          </Form.Item>
          <Form.Item
            name="compType"
            label="行业类型"
            initialValue="GENERAL"
            extra="决定新建租户时预置的行业模版包（素质项库/强制分布/培养方向）"
          >
            <Select options={COMP_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="imChannel" label="IM 渠道" initialValue="dingtalk">
            <Select
              options={[
                { value: 'dingtalk', label: '钉钉' },
                { value: 'wecom', label: '企业微信' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
